import React, { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { PUBLIC_GET_CIAM_STATUS_BY_EMAIL } from 'Queries/Queries';
import energyIcon from 'assets/pics/energy_icon.svg';
import gasIcon from 'assets/pics/gas_icon.svg';
import { useFormik } from 'formik';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';

import { PUBLIC_NO_AUTH_CONFIG } from 'components/ApolloComponent/Apollo';
import {
  getCiamEmailErrorContent,
  getCiamEmailFieldErrorMessage,
} from 'components/CiamEmail/CiamEmailErrors';
import { CustomModal } from 'components/CustomModal';
import { DEBExpertPopupContactSection } from 'components/DebFormComponents/Popup/DEBExpertPopupContactSection';
import {
  buildExpertContactValidationFields,
  getExpertContactFormErrorMessage,
  getExpertEmails,
  setCreateExpertEmailFieldErrors,
} from 'components/DebFormComponents/Popup/expertContact.utils';
import { ErrorBanner } from 'components/ErrorBanner/ErrorBanner';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormField } from 'components/FormHelper/FormField';
import { SectionSubtitle } from 'components/FormHelper/SectionSubtitle';
import { InfoBox } from 'components/InfoBox/InfoBox';
import { LabelCheckbox } from 'components/LabelCheckbox/LabelCheckbox';
import { YNEExpertValidation } from 'components/NEValidation/NEExpertValidation';

import { BRANCH_TYPE, EIV_ENTRY_TYPE, TENANT_WITH_BIRTHDATE } from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';
import { FIRST_QUALIFICATIONS, SECOND_QUALIFICATIONS } from 'global/qualifications';

import { areExpertContactsEnabled } from 'utils/FormUtils';
import { isInstallerRegisteringOnlyForBranch, isRegisteringBranchMain } from 'utils/InstallerUtils';
import { checkCiamEmailByQuery } from 'utils/ciamEmailValidation.utils';
import { getTenantId } from 'utils/tenantUtil';

import './Popup.sass';

export const NENewExpertPopup = ({
  branchSelectionValues,
  popupOpen,
  setPopupOpen,
  mainFormik,
}) => {
  const [ciamEmailError, setCiamEmailError] = useState(null);
  const [isCheckingCiamEmail, setIsCheckingCiamEmail] = useState(false);

  const expertsEmails = useMemo(
    () => getExpertEmails(mainFormik?.values?.experts),
    [mainFormik?.values?.experts]
  );

  const validationSchema = useMemo(() => {
    let schema = YNEExpertValidation(branchSelectionValues);
    if (areExpertContactsEnabled) {
      schema = schema.shape(
        buildExpertContactValidationFields({
          forbiddenMails: [...expertsEmails, mainFormik.values.email],
          isEditing: false,
        })
      );
    }
    return schema;
  }, [branchSelectionValues, expertsEmails]);

  const formik = useFormik({
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      lastName: '',
      firstName: '',
      isExpertEnergyActive: isInstallerRegisteringOnlyForBranch(
        BRANCH_TYPE.ENERGY,
        branchSelectionValues
      ),
      isExpertGasActive: isInstallerRegisteringOnlyForBranch(
        BRANCH_TYPE.GAS,
        branchSelectionValues
      ),
      birthdate: '',
      energyValidityDate: '',
      gasValidityDate: '',
      isExpertOwner: false,
      firstQualificationEnergy: '',
      secondQualificationEnergy: '',
      firstQualificationGas: '',
      secondQualificationGas: '',
      netzportalEmail: '',
      confirmEmail: '',
      newEmail: '',
      telefon: '',
    },
  });

  const [getCiamStatusByEmail] = useLazyQuery(
    PUBLIC_GET_CIAM_STATUS_BY_EMAIL,
    PUBLIC_NO_AUTH_CONFIG
  );

  useEffect(() => {
    if (popupOpen) {
      formik.validateForm();
    }
  }, [popupOpen]);

  useEffect(() => {
    setCiamEmailError(null);
  }, [formik.values.netzportalEmail]);

  const onCancel = () => {
    formik.resetForm();
    setPopupOpen(false);
    setCiamEmailError(null);
  };

  const onSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }

    const email = formik.values.netzportalEmail?.trim();
    if (email) {
      setIsCheckingCiamEmail(true);
      setCiamEmailError(null);
      const ciamResult = await checkCiamEmailByQuery(getCiamStatusByEmail, email);
      setIsCheckingCiamEmail(false);

      if (!ciamResult.isValid) {
        const fieldError = getCiamEmailFieldErrorMessage(ciamResult.errorType);
        setCreateExpertEmailFieldErrors(formik, fieldError);
        setCiamEmailError(getCiamEmailErrorContent(ciamResult.errorType));
        return;
      }
    }

    mainFormik.setFieldValue('experts', [
      ...mainFormik.values.experts,
      {
        ...formik.values,
        completeName: `${formik.values.firstName} ${formik.values.lastName}`,
        id: String(uniqueId()),
      },
    ]);
    formik.resetForm();
    setPopupOpen(false);
    setCiamEmailError(null);
  };

  const secondQualficationGasItems = useMemo(() => {
    const secondQualficationItems = Object.keys(SECOND_QUALIFICATIONS)
      .filter((qualificationsKey) =>
        evaluateCondition(SECOND_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
          branch: BRANCH_TYPE.GAS,
          firstQualificationGas: formik.values.firstQualificationGas,
        })
      )
      .map((qualificationsKey) => SECOND_QUALIFICATIONS[qualificationsKey]);

    if (secondQualficationItems.length === 1) {
      formik.setFieldValue(`secondQualificationGas`, secondQualficationItems[0].value);
    }

    return secondQualficationItems;
  }, [formik.values.firstQualificationGas]);

  const secondQualficationEnergyItems = useMemo(() => {
    const secondQualficationItems = Object.keys(SECOND_QUALIFICATIONS)
      .filter((qualificationsKey) =>
        evaluateCondition(SECOND_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
          branch: BRANCH_TYPE.ENERGY,
          firstQualificationEnergy: formik.values.firstQualificationEnergy,
        })
      )
      .map((qualificationsKey) => SECOND_QUALIFICATIONS[qualificationsKey]);
    if (secondQualficationItems.length === 1) {
      formik.setFieldValue(`secondQualificationEnergy`, secondQualficationItems[0].value);
    }
    return secondQualficationItems;
  }, [formik.values.firstQualificationEnergy]);

  const firstQualificationEnergyItems = useMemo(
    () =>
      Object.keys(FIRST_QUALIFICATIONS)
        .filter((qualificationsKey) =>
          evaluateCondition(FIRST_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
            branch: BRANCH_TYPE.ENERGY,
          })
        )
        .map((qualificationsKey) => FIRST_QUALIFICATIONS[qualificationsKey]),
    []
  );

  const firstQualificationGasItems = useMemo(
    () =>
      Object.keys(FIRST_QUALIFICATIONS)
        .filter((qualificationsKey) =>
          evaluateCondition(FIRST_QUALIFICATIONS[qualificationsKey].whenShouldFieldRender, {
            branch: BRANCH_TYPE.GAS,
          })
        )
        .map((qualificationsKey) => FIRST_QUALIFICATIONS[qualificationsKey]),
    []
  );

  useEffect(() => {
    formik.setFieldValue(`secondQualificationEnergy`, '');
  }, [formik.values.firstQualificationEnergy]);

  useEffect(() => {
    formik.setFieldValue(`secondQualificationGas`, '');
  }, [formik.values.firstQualificationGas]);

  const modalContent = () => (
    <div className="ne-popup popup-content">
      <div className="ne-popup-field">
        <div className="flex-row">
          <div>Für die Sparte:</div>
          {branchSelectionValues.isEnergyActive && (
            <LabelCheckbox
              label="Strom"
              id="isExpertEnergyActive"
              name="isExpertEnergyActive"
              error={Boolean(formik.errors?.noBranchSelected)}
              icon={energyIcon}
              value={formik.values.isExpertEnergyActive}
              onChange={formik.handleChange}
            />
          )}
          {branchSelectionValues.isGasActive && (
            <LabelCheckbox
              label="Gas"
              id="isExpertGasActive"
              name="isExpertGasActive"
              error={Boolean(formik.errors?.noBranchSelected)}
              icon={gasIcon}
              value={formik.values.isExpertGasActive}
              onChange={formik.handleChange}
            />
          )}
        </div>
      </div>
      {!formik.errors.noBranchSelected && (
        <>
          <div className="ne-popup-field">
            <FormField name="lastName" formik={formik} label="Nachname*" oldValue="" fullWidth />
          </div>
          <div className="ne-popup-field">
            <FormField name="firstName" formik={formik} label="Vorname*" oldValue="" fullWidth />
          </div>
          {areExpertContactsEnabled && (
            <>
              <DEBExpertPopupContactSection formik={formik} />
              {ciamEmailError && <ErrorBanner>{ciamEmailError}</ErrorBanner>}
            </>
          )}
          {TENANT_WITH_BIRTHDATE.includes(getTenantId()) && (
            <div className="ne-popup-field">
              <FormField
                name="birthdate"
                formik={formik}
                label="Geburtsdatum*"
                oldValue=""
                fullWidth
              />
            </div>
          )}
          {formik.values.isExpertEnergyActive &&
            branchSelectionValues.branchTypeEnergy === EIV_ENTRY_TYPE.GUEST && (
              <div className="ne-popup-field">
                <FormField
                  name="energyValidityDate"
                  formik={formik}
                  label="Strom-Ausweis gültig bis*"
                  oldValue=""
                  fullWidth
                />
              </div>
            )}
          {formik.values.isExpertGasActive &&
            branchSelectionValues.branchTypeGas === EIV_ENTRY_TYPE.GUEST && (
              <div className="ne-popup-field">
                <FormField
                  name="gasValidityDate"
                  formik={formik}
                  label="Gas-Ausweis gültig bis*"
                  oldValue=""
                  fullWidth
                />
              </div>
            )}
          {((formik.values.isExpertEnergyActive &&
            branchSelectionValues.branchTypeEnergy === EIV_ENTRY_TYPE.MAIN) ||
            (formik.values.isExpertGasActive &&
              branchSelectionValues.branchTypeGas === EIV_ENTRY_TYPE.MAIN)) && (
            <div className="ne-popup-field">
              <FormField
                name="isExpertOwner"
                formik={formik}
                type="radio"
                label="Die verantwortliche Fachkraft ist Inhaber/Geschäftsführer*?"
                radioClassName="flex-col"
                radioGroupClassName="flex-row"
              />
            </div>
          )}
        </>
      )}

      {isRegisteringBranchMain(BRANCH_TYPE.ENERGY, branchSelectionValues) &&
        formik.values.isExpertEnergyActive && (
          <>
            {formik.values.isExpertEnergyActive && formik.values.isExpertGasActive && (
              <SectionSubtitle>Qualifikationen – Strom</SectionSubtitle>
            )}
            <div className="popup-content__popup-field">
              <FormField
                name="firstQualificationEnergy"
                formik={formik}
                type="select"
                items={firstQualificationEnergyItems}
                label="Qualifikation wählen*"
                oldValue=""
                fullWidth
              />
            </div>
            {secondQualficationEnergyItems.length === 1 && (
              <div className="popup-content__popup-field">
                <div className="input-field">
                  <InfoBox>{secondQualficationEnergyItems[0].label}</InfoBox>
                </div>
              </div>
            )}
            {secondQualficationEnergyItems.length > 1 && (
              <div className="popup-content__popup-field">
                <FormField
                  name="secondQualificationEnergy"
                  formik={formik}
                  type="select"
                  items={secondQualficationEnergyItems}
                  label="Qualifikation wählen*"
                  oldValue=""
                  fullWidth
                />
              </div>
            )}
          </>
        )}

      {isRegisteringBranchMain(BRANCH_TYPE.GAS, branchSelectionValues) &&
        formik.values.isExpertGasActive && (
          <>
            {formik.values.isExpertEnergyActive && formik.values.isExpertGasActive && (
              <SectionSubtitle>Qualifikationen – Gas</SectionSubtitle>
            )}
            <div className="popup-content__popup-field">
              <FormField
                name="firstQualificationGas"
                formik={formik}
                type="select"
                items={firstQualificationGasItems}
                label="Qualifikation wählen*"
                oldValue=""
                fullWidth
              />
            </div>
            {secondQualficationGasItems.length === 1 && (
              <div className="popup-content__popup-field">
                <div className="input-field">
                  <InfoBox>{secondQualficationGasItems[0].label}</InfoBox>
                </div>
              </div>
            )}
            {secondQualficationGasItems.length > 1 && (
              <div className="popup-content__popup-field">
                <FormField
                  name="secondQualificationGas"
                  formik={formik}
                  type="select"
                  items={secondQualficationGasItems}
                  label="Qualifikation wählen*"
                  oldValue=""
                  fullWidth
                />
              </div>
            )}
          </>
        )}
      {/* <FFormDebug values={formik.values} errors={formik.errors} /> */}
      {/* <FFormDebug values={branchSelectionValues} errors={formik.errors} /> */}
    </div>
  );

  const getErrorMessage = (errors, isValid) => {
    const contactError = getExpertContactFormErrorMessage(errors);
    if (contactError) {
      return contactError;
    }
    if (errors.noBranchSelected) {
      return errors.noBranchSelected;
    }
    if (!isValid) {
      return 'Füllen Sie das Formular vollständig aus';
    }
    return '';
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Fachkraft hinzufügen"
      closeCancelEventModal={onCancel}
      ctaButtons={
        <FormActionButtons
          hasErrors={Object.keys(formik.errors).length > 0 || Boolean(ciamEmailError)}
          titleLeft="Abbrechen"
          titleRight="Hinzufügen"
          onClickLeft={onCancel}
          onClickRight={onSubmit}
          loading={isCheckingCiamEmail}
          errorMessage={getErrorMessage(formik.errors, formik.isValid)}
        />
      }
      content={modalContent()}
    />
  );
};

NENewExpertPopup.propTypes = {
  branchSelectionValues: PropTypes.object,
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  mainFormik: PropTypes.object,
};

NENewExpertPopup.defaultProps = {
  branchSelectionValues: {},
  popupOpen: false,
  setPopupOpen: () => {},
  mainFormik: {},
};
